import {
  Action,
  Driver,
  Journey,
  JourneyCollection,
  Shipment,
  Waypoint,
} from 'interfaces/journey';
import {
  createContext,
  JSX,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Point } from './components/map';
import useUserCityPosition from 'hooks/useUserCity';
import { JourneyResponse, useAddShipment, useJourney } from 'services/journeys';
import {
  buildShipmentsById,
  FlaggedShipment,
  getFlaggedShipments,
} from 'services/journeys/shipmentHelpers';
import { getById, useDrivers } from 'services/DriverService';
import { polyline } from 'leaflet';
import {
  buildCenterDeliveries,
  isCollectionDelivery,
} from './centerDeliveries';

const JourneyContext = createContext<JourneyDashboardContext>(null!);

interface JorneyProviderProps {
  id: string;
  children?: JSX.Element | JSX.Element[];
}

interface JourneyDashboardContext extends JourneyResponse {
  shipmentsById: Record<string, Shipment>;
  collectionsById: Record<number, JourneyCollection>;
  deliveryByCollection: Record<number, Action | undefined>;
  shipmentCount: number;
  shipmentsWithNoActions: Shipment[];
  flaggedShipments: FlaggedShipment[];
  center: Point | undefined;
  loading: boolean;
  hasError: boolean;
  setCenter: React.Dispatch<React.SetStateAction<Point | undefined>>;
  drivers: Driver[];
  refetch: () => void;
  // TODO: Lucas added this just to be able to deploy the branch, but he doesn't know if it's actually a real field.
  message?: string;
}

export const useJourneyDashboardContext = (): JourneyDashboardContext =>
  useContext<JourneyDashboardContext>(JourneyContext);

export const JourneyProvider = (props: JorneyProviderProps) => {
  const {
    data: journey,
    isLoading,
    isError,
    refetch,
    isSuccess,
  } = useJourney(props.id);
  const [center, setCenter] = useState<Point | undefined>();
  const { data: drivers } = useDrivers();
  const [parsedJourney, setParsedJourney] = useState<Journey>();

  useEffect(() => {
    if (journey?.driver && journey.driver.id && !journey.driver.email) {
      getById(journey.driver.id).then((driver) => {
        if (driver) {
          setParsedJourney({
            ...journey,
            driver: {
              ...driver,
              fullname: `${driver.name} ${driver.lastName}`,
            },
          });
        }
      });
    } else {
      setParsedJourney(journey);
    }
  }, [journey, isSuccess]);

  const shipmentsById = useMemo(
    () => buildShipmentsById(journey?.shipments),
    [journey, isSuccess],
  );

  const collectionsById = useMemo(
    () =>
      Object.fromEntries(
        (journey?.collections ?? []).map((collection) => [
          collection.id,
          collection,
        ]),
      ) as Record<number, JourneyCollection>,
    [journey, isSuccess],
  );

  const parsedActionsWithoutWaypoints = useMemo(
    () =>
      journey?.actionsWithoutWaypoints?.reduce<Waypoint[]>((acc, action) => {
        if (!action.routeShipmentId) return acc;
        const shipment: Shipment = shipmentsById?.[action.routeShipmentId];
        if (shipment) {
          acc.push({
            actions: [action],
            address:
              action.actionType === 'deliver'
                ? shipment.destinationAddress
                : shipment.originAddress,
          });
        }
        return acc;
      }, []) ?? [],
    [journey, shipmentsById, isSuccess],
  );

  const deliveryByCollection = useMemo(() => {
    const byCollection: Record<number, Action | undefined> = {};
    [
      ...(journey?.waypoints ?? []).flatMap(
        (waypoint) => waypoint.actions ?? [],
      ),
      ...(journey?.actionsWithoutWaypoints ?? []),
    ]
      .filter(isCollectionDelivery)
      .forEach((action) => {
        byCollection[action.collectionId as number] = action;
      });
    return byCollection;
  }, [journey, isSuccess]);

  // Estado de ms-collections: ingresada aunque la ruta cerrada no se haya enterado, o sin
  // retirar (no hay nada que entregar en el centro).
  const idsWithStatus = (statuses: string[]) =>
    new Set(
      (journey?.collections ?? [])
        .filter((c) => statuses.includes(c.collectionStatus ?? ''))
        .map((c) => c.id),
    );
  const receivedCollectionIds = useMemo(
    () => idsWithStatus(['received']),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [journey],
  );
  const notCollectedIds = useMemo(
    () => idsWithStatus(['not_collected', 'cancelled']),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [journey],
  );

  const parsedWaypoints = useMemo(
    () =>
      journey?.waypoints
        ? [
            ...journey.waypoints.map((waypoint) => {
              const deliveries = (waypoint.actions ?? []).filter(
                isCollectionDelivery,
              );
              if (deliveries.length === 0) return waypoint;
              return {
                ...waypoint,
                actions: waypoint.actions?.filter(
                  (action) => !isCollectionDelivery(action),
                ),
                centerDeliveries: buildCenterDeliveries(
                  deliveries,
                  collectionsById,
                  receivedCollectionIds,
                  notCollectedIds,
                ),
              };
            }),
            ...parsedActionsWithoutWaypoints,
          ]
        : [],
    [
      journey,
      parsedActionsWithoutWaypoints,
      collectionsById,
      receivedCollectionIds,
      notCollectedIds,
      isSuccess,
    ],
  );

  const shipmentsWithNoActions = useMemo(() => {
    if (!journey?.shipments) return [];

    const referencedShipmentIds = new Set<number>();

    // Collect shipment IDs from waypoints
    journey?.waypoints?.forEach((waypoint) => {
      waypoint.actions?.forEach((action) => {
        if (action.routeShipmentId) {
          referencedShipmentIds.add(action.routeShipmentId);
        }
      });
    });

    // Collect shipment IDs from actionsWithoutWaypoints
    journey?.actionsWithoutWaypoints?.forEach((action) => {
      if (action.routeShipmentId) {
        referencedShipmentIds.add(action.routeShipmentId);
      }
    });

    // Return shipments not referenced by any action
    return journey.shipments.filter(
      (shipment) => shipment.id && !referencedShipmentIds.has(shipment.id),
    );
  }, [journey, isSuccess]);

  const flaggedShipments = useMemo(
    () => getFlaggedShipments(parsedWaypoints, shipmentsById),
    [parsedWaypoints, shipmentsById],
  );

  const context = useMemo<JourneyDashboardContext>(
    () => ({
      ...parsedJourney,
      id: parsedJourney?.journeyId || parsedJourney?.id,
      shipmentsById,
      collectionsById,
      deliveryByCollection,
      shipmentCount: parsedJourney?.shipments?.length ?? 0,
      estimatedDistance: parsedJourney?.estimatedDistance ?? undefined,
      estimatedTime: parsedJourney?.estimatedTime ?? undefined,
      waypoints: parsedWaypoints,
      actionsWithoutWaypoints: (
        parsedJourney?.actionsWithoutWaypoints ?? []
      ).filter((action) => !isCollectionDelivery(action)),
      shipmentsWithNoActions,
      flaggedShipments,
      loading: isLoading,
      hasError: isError,
      center,
      setCenter,
      drivers,
      refetch,
    }),
    [
      parsedJourney,
      shipmentsById,
      collectionsById,
      deliveryByCollection,
      parsedWaypoints,
      shipmentsWithNoActions,
      flaggedShipments,
      isLoading,
      isSuccess,
      isError,
      center,
      drivers,
    ],
  );

  return (
    <JourneyContext.Provider value={context}>
      {props.children}
    </JourneyContext.Provider>
  );
};
